import DOMPurify from 'dompurify';
import { Autolinker } from 'autolinker';
import { TEAMS_LINK_ESCAPED_REGEXP } from './constants';

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export const makeAnchor = (url: string, label: string): string => {
  const safeUrl = escapeHtml(url);
  const safeLabel = escapeHtml(label);
  const classes =
    'bg-transparent text-primary-100 hover:underline disabled:text-neutral-60 disabled:no-underline focus:outline-hidden rounded-lg text-body-m wrap-break-word';

  return `<a href="${safeUrl}" class="${classes}" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;
};

/**
 * Splits a string on existing <a …>…</a> tags and runs Autolinker only on the
 * text segments (i.e. parts that are NOT already wrapped in an anchor).
 * This avoids double-linking URLs produced by Pass 1 (Teams-style patterns).
 */
const linkifyTextSegments = (html: string): string => {
  // Split on <a ...>...</a> spans, keeping the delimiters in the array.
  const parts = html.split(/(<a [\s\S]*?<\/a>)/);

  return parts
    .map((part) => {
      // Keep already-linkified anchors as-is.
      if (part.startsWith('<a ')) {
        return part;
      }

      // For plain-text segments, run Autolinker so that www.*, emails, etc. get linkified.
      return Autolinker.link(part, {
        urls: { schemeMatches: true, tldMatches: true },
        email: true,
        phone: false,
        mention: false,
        hashtag: false,
        sanitizeHtml: true,
        replaceFn: (match) => {
          const href = match.getAnchorHref();
          const text = match.getAnchorText();

          return makeAnchor(href, text);
        },
      });
    })
    .join('');
};

export const getLinkifiedText = (value?: string): string => {
  if (!value) {
    return '';
  }

  // Sanitize all HTML in the input first to prevent XSS — identical to Autolinker's sanitizeHtml behaviour.
  const sanitized = escapeHtml(value);

  // Pass 1: replace "text&lt;url&gt;" combo patterns with anchors where the captured text is the label.
  // The regex consumes a leading separator char (\r, \n, |) which we preserve.
  // e.g. "Need help?&lt;https://aka.ms/...&gt;" → <a href="https://aka.ms/...">Need help?</a>
  const pass1 = sanitized.replace(TEAMS_LINK_ESCAPED_REGEXP, (fullMatch: string, text: string, url: string) => {
    const separator = fullMatch[0];
    const label = text?.trim() ? DOMPurify.sanitize(text.trim()) : url;

    return `${separator}${makeAnchor(url, label)}`;
  });

  // Pass 2: linkify remaining text segments (www.*, emails, plain https?:// URLs, etc.)
  // using Autolinker, while leaving Pass-1 anchors untouched.
  return linkifyTextSegments(pass1);
};
